import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  nl: { naam: "Naam", email: "E-mail", onderwerp: "Onderwerp (optioneel)", bericht: "Bericht", verzenden: "Verstuur bericht", verzenden_bezig: "Bezig met versturen…", success_title: "Bedankt voor je bericht!", success_body: "We nemen zo snel mogelijk contact op via het opgegeven e-mailadres.", error_generic: "Kon je bericht niet versturen. Probeer het zo nog eens.", verplicht: "Verplicht" },
  de: { naam: "Name", email: "E-Mail", onderwerp: "Betreff (optional)", bericht: "Nachricht", verzenden: "Nachricht senden", verzenden_bezig: "Wird gesendet…", success_title: "Danke für Ihre Nachricht!", success_body: "Wir melden uns so schnell wie möglich unter der angegebenen E-Mail-Adresse.", error_generic: "Nachricht konnte nicht gesendet werden. Bitte gleich erneut versuchen.", verplicht: "Erforderlich" },
  en: { naam: "Name", email: "Email", onderwerp: "Subject (optional)", bericht: "Message", verzenden: "Send message", verzenden_bezig: "Sending…", success_title: "Thanks for your message!", success_body: "We'll get back to you as soon as possible at the email address you provided.", error_generic: "Couldn't send your message. Please try again shortly.", verplicht: "Required" },
};

export const ContactForm = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || "nl";
  const L = LABELS[lang] || LABELS.nl;

  const [form, setForm] = useState({ naam: "", email: "", onderwerp: "", bericht: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${BACKEND}/api/contact`, form);
      setStatus("success");
      setForm({ naam: "", email: "", onderwerp: "", bericht: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || L.error_generic);
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success"
        className="bg-[#EEF7EF] border border-[#B7DCB6] rounded-2xl p-6 flex items-start gap-3"
      >
        <CheckCircle2 className="w-6 h-6 text-[#3a9440] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-1">
            {L.success_title}
          </h3>
          <p className="text-[#3E5871] font-[Inter]">{L.success_body}</p>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="bg-white rounded-2xl border border-[#E4EEF5] shadow-sm p-6 sm:p-8 space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
            {L.naam} <span className="text-[#CE1B2A]">*</span>
          </label>
          <input
            data-testid="contact-naam"
            required
            type="text"
            value={form.naam}
            onChange={setField("naam")}
            className="w-full bg-white border border-[#E4EEF5] rounded-xl px-3 py-2.5 font-[Inter] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
            {L.email} <span className="text-[#CE1B2A]">*</span>
          </label>
          <input
            data-testid="contact-email"
            required
            type="email"
            value={form.email}
            onChange={setField("email")}
            className="w-full bg-white border border-[#E4EEF5] rounded-xl px-3 py-2.5 font-[Inter] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
          {L.onderwerp}
        </label>
        <input
          data-testid="contact-onderwerp"
          type="text"
          value={form.onderwerp}
          onChange={setField("onderwerp")}
          className="w-full bg-white border border-[#E4EEF5] rounded-xl px-3 py-2.5 font-[Inter] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
        />
      </div>

      <div>
        <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
          {L.bericht} <span className="text-[#CE1B2A]">*</span>
        </label>
        <textarea
          data-testid="contact-bericht"
          required
          rows={6}
          value={form.bericht}
          onChange={setField("bericht")}
          className="w-full bg-white border border-[#E4EEF5] rounded-xl px-3 py-2.5 font-[Inter] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition resize-y"
        />
      </div>

      {status === "error" && (
        <div className="p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] flex items-start gap-2">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span className="font-[Inter] text-sm">{errorMsg}</span>
        </div>
      )}

      <button
        data-testid="contact-submit"
        type="submit"
        disabled={status === "sending"}
        className="ll-btn-primary w-full sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {L.verzenden_bezig}
          </>
        ) : (
          <>
            <Mail className="w-4 h-4" />
            {L.verzenden}
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
